

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import he from "he";
import "../../styles/global.css";
import { getPrefetched, prefetchStory } from "./storyPrefetch";

const SLIDE_DURATION_MS = 10000;
let hasHydratedOnce = false;

export default function Story({data}) {

  const ssrData = data?.[0]?.story || {}
  const ssrStoryData =  ssrData?.storyData
  const ssrRelatedStoriesData =  ssrData?.relatedStoriesData
 
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const placeholderThumb = location.state?.thumb || null;

  const slugArray = slug.split("-");
  const story_id = slugArray.at(-1);
  const feature_id = slugArray.at(-2);

  const [story, setStory] = useState(ssrStoryData);
  const [relatedStories, setRelatedStories] = useState(ssrRelatedStoriesData);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  
  const [bgA, setBgA] = useState(placeholderThumb ? encodeURI(placeholderThumb) : "");
  const [bgB, setBgB] = useState("");
  const [activeBg, setActiveBg] = useState("A");

  const touchStartX = useRef(null);

  useEffect(() => {
     
    if (ssrStoryData && ssrRelatedStoriesData && hasHydratedOnce) {
         hasHydratedOnce = true;
         setLoading(false)
         return;
    }
   
    let cancelled = false;
   
    async function load() {
      setLoading(true);
      setError(null);
      setIndex(0);
      try {
        const cached = getPrefetched(`/${category}/${slug}`);
        const json = cached
          ? await cached
          : await fetch(
              `https://www.teluguone.com/news_api/newsDetailedWebStoresJson?story_id=${story_id}&feature_id=${feature_id}`,
              { cache: "no-store" }
            ).then((res) => {
                if (!res.ok) throw new Error("Failed to load story");
                return res.json();
              });
        if (!json) throw new Error("Failed to load story");
        const normalized = json.detailed_web_stories;
        if (!cancelled) {
          if (!normalized || !normalized.length) setError("No slides found for this story.");
          setStory(normalized);
          setRelatedStories(json.related_web_stories || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => { cancelled = true; };
  }, [story_id, feature_id]);

  const slides = story ? story : [];
  const total = slides.length + 1;
  const isEndCard = index === slides.length;
  const current = !isEndCard ? slides[index] : null;

  // preload + swap the inactive bg layer, then flip active
  useEffect(() => {
    if (!current?.stimg) return;
    const url = current.stimg;
    const img = new Image();
    img.onload = () => {
      if (activeBg === "A") { setBgB(encodeURI(url || "")); setActiveBg("B"); }
      else { setBgA( encodeURI(url || "")); setActiveBg("A"); }
    };
    img.src = url;
   
  }, [current?.stimg]);

 
  useEffect(() => {
    setImgLoaded(false);
  }, [index]);

  
  useEffect(() => {
    const nextSlide = slides[index + 1];
    if (!nextSlide?.stimg) return;
    const img = new Image();
    img.src = nextSlide.stimg;
  }, [index, slides]);

  
  useEffect(() => {
    if (!slides.length || !slides[0]?.stimg) return;
    let link;
    try {
      const origin = new URL(slides[0].stimg).origin;
      link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    } catch {
      
    }
    return () => {
      if (link) document.head.removeChild(link);
    };
  }, [slides]);

  const next = useCallback(() => {
    setIndex((i) => (i < total - 1 ? i + 1 : i));
  }, [total]);
  const prev = useCallback(() => {
    setIndex((i) => (i - 1 >= 0 ? i - 1 : i));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") {
        if (shareOpen) setShareOpen(false);
        else navigate(`/${category}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, navigate, category, shareOpen]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    setPaused(false);
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 40) diff < 0 ? next() : prev();
    touchStartX.current = null;
  };

  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }, []);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // const goBack = useCallback(
  //   (e) => {
  //     e?.stopPropagation();
  //     // go to the previous page if we actually navigated here from within the app,
  //     // otherwise fall back to the category listing
  //     if (window.history.state && window.history.state.idx > 0) {
  //       navigate(-1);
  //     } else {
  //       navigate(`/${category}`);
  //     }
  //   },
  //   [navigate, category]
  // );

  const goBack = useCallback(
  (e) => {
    e?.stopPropagation();

    // Tell the previous page to scroll to top
    sessionStorage.setItem("forceScrollTop", "true");

    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/${category}`);
    }
  },
  [navigate, category]
);


useEffect(() => {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  return () => {
    window.history.scrollRestoration = "auto";
  };
}, []);



  const copyToClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
   
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.top = "-1000px";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const openShare = (e) => {
    e?.stopPropagation();
    setShareOpen(true);
  };

  const closeShare = (e) => {
    e?.stopPropagation();
    setShareOpen(false);
  };

  const shareVia = async (platform, e) => {
    e?.stopPropagation();
    const url = window.location.href;
    const title = he.decode(current?.title || "") || "TeluguOne Web Story";

    if (platform === "whatsapp") {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    } else if (platform === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    } else if (platform === "x") {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    } else if (platform === "copy") {
      const copied = await copyToClipboard(url);
      showToast(copied ? "Link copied!" : "Couldn't copy link");
    } else if (platform === "more" && navigator.share) {
      try {
        await navigator.share({ title, text: title, url });
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      }
    }
    setShareOpen(false);
  };

  


  if (loading) {
    return (
      <div className="story-viewer">
        {placeholderThumb && (
          <>
            <div
              className="story-bg is-active"
              style={{ backgroundImage: `url(${encodeURI(placeholderThumb)})` }}
            />
            <div className="story-bg-veil" />
          </>
        )}
        <div
          className="story-frame story-frame-skeleton"
          style={
            placeholderThumb
              ? {
                  backgroundImage: `url(${encodeURI(placeholderThumb)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "blur(3px) brightness(.65)",
                  animation: "none",
                }
              : undefined
          }
        />
      </div>
    );
  }

  if (error || !story || !slides.length) {
    return (
      <div className="story-viewer">
        <div className="story-error">
          <h2>Story not available</h2>
          <p>{error || "This story couldn't be loaded."}</p>
          <Link to={`/${category}`} className="story-error-back">← Back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="story-viewer">
     
      <div
        className={`story-bg ${activeBg === "A" ? "is-active" : ""}`}
        style={{ backgroundImage: bgA ? `url(${bgA})` : "none" }}
      />
      <div
        className={`story-bg ${activeBg === "B" ? "is-active" : ""}`}
        style={{ backgroundImage: bgB ? `url(${bgB})` : "none" }}
      />
      <div className="story-bg-veil" />

      {isEndCard ? (
        <div className="story-shell">
          <button className="story-nav-btn prev" onClick={prev}>‹</button>
          <div className="story-frame story-end-card">
            <a href="https://www.teluguone.com"  target="_blank" rel="noopener noreferrer" className="story-end-logo">
              <img src="/webisodes/assets/images/tone_logo.webp" alt="TeluguOne" />
            </a>
            {/* <h2>Thanks for reading <span className="heart">♥</span></h2>
            <p className="story-end-sub">Keep exploring more stories</p> */}

            <div className="story-related-grid">
              {relatedStories && relatedStories.slice(0, 4).map((item) => {
                const relTo = `${item.url_link.substring(0,item.url_link.lastIndexOf('.html')) || ''}`;
                return (
                  <Link
                    key={item.story_id}
                    to={relTo}
                    className="story-related-item"
                    state={{ thumb: item.stimg }}
                    onMouseEnter={() => prefetchStory(relTo)}
                    onTouchStart={() => prefetchStory(relTo)}
                  >
                    <div className="story-related-thumb">
                      <img src={item.stimg} alt={he.decode(item.title || "")} />
                    </div>
                    <span>{he.decode(item.fulldesc || "")}</span>
                  </Link>
                );
              })}
            </div>



            <div className="story-end-actions">
              <Link to="/" className="story-home-btn">Go To Home →</Link>
              <button className="story-replay-btn" onClick={() => setIndex(0)}>↻ Replay Webisodes</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="story-shell">
          <button
            className="story-nav-btn prev"
            onClick={prev}
            aria-label="Previous"
            disabled={index === 0}
          >‹</button>

          <div
            className="story-frame"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onMouseDown={() => setPaused(true)}
            onMouseUp={() => setPaused(false)}
          >
            <div className="progress">
              {slides && slides.map((_, i) => (
                <span key={i} className={i < index ? "done" : i === index ? "active" : ""}>
                  {i === index && (
                    <i
                      key={index}
                      style={{
                        animationDuration: `${SLIDE_DURATION_MS}ms`,
                        animationPlayState: paused ? "paused" : "running",
                      }}
                      onAnimationEnd={next}
                    />
                  )}
                </span>
              ))}
            </div>

            <div className="story-topbar">
              <a href="https://www.teluguone.com"  target="_blank" rel="noopener noreferrer" className="brand-tag">
                <img src="/webisodes/assets/images/tone_logo.webp" alt="" />
                <span>TeluguOne</span>
              </a>
              <div className="story-topbar-right">
                <button
                  className="story-back-btn"
                  onClick={goBack}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  aria-label="Go back"
                >
                  ‹
                </button>
                <button
                  className="story-close"
                  onClick={openShare}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  aria-label="Share"
                >
                  ↗
                </button>
              </div>
            </div>

            {toast && <div className="story-toast">{toast}</div>}

            {shareOpen && (
              <div
                className="story-share-overlay"
                onClick={closeShare}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <div className="story-share-sheet" onClick={(e) => e.stopPropagation()}>
                  <div className="story-share-sheet-header">
                    <h3>Share story</h3>
                    <button className="story-share-cancel-btn" onClick={closeShare} aria-label="Cancel">✕</button>
                  </div>
                  <div className="story-share-options">
                    <button className="story-share-option" onClick={(e) => shareVia("whatsapp", e)}>
                      <span className="story-share-option-icon story-share-icon-whatsapp">
                        <svg viewBox="0 0 24 24" fill="white"><path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.9 11.86L4 20l4.26-1.11a7.93 7.93 0 0 0 3.79.96h0a7.94 7.94 0 0 0 5.55-13.53zM12.05 18.4a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.53.66.68-2.47-.16-.25a6.6 6.6 0 1 1 12.26-3.5 6.56 6.56 0 0 1-6.65 6.62zm3.6-4.93c-.2-.1-1.17-.58-1.35-.64s-.31-.1-.44.1-.5.64-.61.77-.23.15-.42.05a5.4 5.4 0 0 1-1.59-.98 6 6 0 0 1-1.1-1.37c-.12-.2 0-.3.09-.4.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33a.36.36 0 0 0 0-.35c-.05-.1-.44-1.06-.6-1.45-.16-.38-.32-.33-.44-.34h-.38a.72.72 0 0 0-.52.24 2.2 2.2 0 0 0-.68 1.63 3.8 3.8 0 0 0 .8 2.02c.1.13 1.38 2.1 3.35 2.95.28.14.74.31 1.12.42a2.7 2.7 0 0 0 1.24.08c.38-.06 1.17-.48 1.33-.94.17-.46.17-.86.12-.94-.05-.09-.18-.14-.38-.24z"/></svg>
                      </span>
                      WhatsApp
                    </button>
                    <button className="story-share-option" onClick={(e) => shareVia("facebook", e)}>
                      <span className="story-share-option-icon story-share-icon-facebook">
                        <svg viewBox="0 0 24 24" fill="white"><path d="M13.5 21v-7.5h2.5l.5-3h-3V8.5c0-.87.24-1.46 1.49-1.46H16.5V4.34C16.24 4.3 15.36 4.22 14.33 4.22c-2.15 0-3.62 1.31-3.62 3.72V10.5H8.5v3h2.21V21h2.79z"/></svg>
                      </span>
                      Facebook
                    </button>
                    <button className="story-share-option" onClick={(e) => shareVia("x", e)}>
                      <span className="story-share-option-icon story-share-icon-x">
                        <svg viewBox="0 0 24 24" fill="white"><path d="M17.53 3H20.6l-6.7 7.65L21.8 21h-6.18l-4.84-6.34L5.2 21H2.13l7.17-8.19L1.9 3h6.34l4.38 5.79zM16.44 19.1h1.7L7.64 4.8H5.8z"/></svg>
                      </span>
                      X
                    </button>
                    <button className="story-share-option" onClick={(e) => shareVia("copy", e)}>
                      <span className="story-share-option-icon story-share-icon-copy">
                        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07l-1.5 1.5"/><path d="M14 10a5 5 0 0 0-7.07 0L4.1 12.83a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>
                      </span>
                      Copy Link
                    </button>
                    {typeof navigator !== "undefined" && navigator.share && (
                      <button className="story-share-option" onClick={(e) => shareVia("more", e)}>
                        <span className="story-share-option-icon story-share-icon-more">
                          <svg viewBox="0 0 24 24" fill="white"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
                        </span>
                        More
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <img
              key={index}
              src={current.stimg}
              alt={he.decode(current.title || "")}
              className={`story-img ${imgLoaded ? "is-loaded" : ""}`}
              loading="eager"
              decoding="async"
              fetchpriority={index === 0 ? "high" : "auto"}
              onLoad={() => setImgLoaded(true)}
            />
            {!imgLoaded && <div className="story-img-skeleton" />}

            <div className="story-tap-zones">
              <div className="tap-zone tap-zone-left" onClick={prev} />
              <div className="tap-zone tap-zone-right" onClick={next} />
            </div>

            {current.title && (
              <div className="story-caption-scrim">
                <div className="story-caption-panel">
                  <div className="story-caption">{he.decode(current.title || "")}</div>
                </div>
              </div>
            )}
          </div>

          <button
            className="story-nav-btn next"
            onClick={next}
            aria-label="Next"
            disabled={index >= total - 1}
          >›</button>
        </div>
      )}
    </div>
  );
}

